// ProductSellingPriceRuleManager.jsx
import React, { Component } from "react";
import CartContext from "./CartContext";
import Swal from "sweetalert2";
import axios from "axios";
import { LoadSpinner } from "./LoadSpinner";
import {
  Table,
  Modal,
  Form,
  Row,
  Col,
  Alert,
  FloatingLabel,
} from "react-bootstrap";
import { Button } from "react-bootstrap";
import { BiRefresh } from "react-icons/bi";

// exact base as you requested
const API_BASE = (process.env.REACT_APP_API || "") + "SellingPriceRules";

export default class ProductSellingPriceRuleManager extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      rules: [],
      loading: true,
      showModal: false,
      isEditing: false,
      editRule: this.emptyRule(),
      message: null,
      saving: false,
    };
  }

  emptyRule = () => ({
    sellingPriceBusinessRuleId: null,
    name: "",
    description: "",
    priority: 10,
    isActive: true,
    sellingPriceRuleTiers: [],
  });

  componentDidMount() {
    this.loadRules();
  }

  // -----------------------
  // Helpers (camelCase payloads) - NO fixedSellingPrice anywhere
  // -----------------------
  normalizeTiersForPayload = (tiers = []) =>
    (tiers || []).map((t, idx) => ({
      sellingPriceRuleTierId: t.sellingPriceRuleTierId || null,
      minQtyInclusive:
        t.minQtyInclusive == null || t.minQtyInclusive === ""
          ? null
          : Number(t.minQtyInclusive),
      maxQtyInclusive:
        t.maxQtyInclusive == null || t.maxQtyInclusive === ""
          ? null
          : Number(t.maxQtyInclusive),
      discountPercentOfMrp:
        t.discountPercentOfMrp == null || t.discountPercentOfMrp === ""
          ? null
          : Number(t.discountPercentOfMrp),
      // fixedSellingPrice intentionally omitted (deprecated)
      seqNo: t.seqNo == null || t.seqNo === "" ? idx + 1 : Number(t.seqNo),
    }));

  validateTiers = (rule) => {
    const tiers = (rule.sellingPriceRuleTiers || []).map((t, idx) => ({
      min:
        t.minQtyInclusive == null || t.minQtyInclusive === ""
          ? Number.NEGATIVE_INFINITY
          : Number(t.minQtyInclusive),
      max:
        t.maxQtyInclusive == null || t.maxQtyInclusive === ""
          ? Number.POSITIVE_INFINITY
          : Number(t.maxQtyInclusive),
      seq: Number(t.seqNo || idx + 1),
      idx,
    }));

    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (!Number.isFinite(t.seq) || t.seq <= 0) {
        return {
          ok: false,
          message: `Tier #${i + 1}: SeqNo must be a positive integer.`,
        };
      }
      if (t.min > t.max) {
        return {
          ok: false,
          message: `Tier #${i + 1}: Min Qty cannot be greater than Max Qty.`,
        };
      }
    }

    // check overlaps by sorting by min then seq
    const sorted = tiers.slice().sort((a, b) => {
      if (a.min !== b.min) return a.min - b.min;
      return a.seq - b.seq;
    });

    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const cur = sorted[i];
      // overlapping if prev.max >= cur.min
      if (prev.max >= cur.min) {
        return {
          ok: false,
          message: `Tier overlap detected between Seq ${prev.seq} and Seq ${cur.seq}.`,
        };
      }
    }

    return { ok: true, message: "" };
  };

  // build payload for POST (create) — omit IDs
  buildCreatePayload = (rule) => {
    return {
      name: (rule.name || "").trim(),
      description: rule.description || null,
      priority: Number(rule.priority || 0),
      isActive: !!rule.isActive,
      storeId: this.context?.storeId,
      sellingPriceRuleTiers: (rule.sellingPriceRuleTiers || []).map(
        (t, idx) => ({
          // do NOT include sellingPriceRuleTierId (omit) when creating
          minQtyInclusive:
            t.minQtyInclusive == null ? null : Number(t.minQtyInclusive),
          maxQtyInclusive:
            t.maxQtyInclusive == null ? null : Number(t.maxQtyInclusive),
          discountPercentOfMrp:
            t.discountPercentOfMrp == null
              ? null
              : Number(t.discountPercentOfMrp),
          seqNo: t.seqNo == null ? idx + 1 : Number(t.seqNo),
        })
      ),
    };
  };

  // build payload for PUT (update) — include IDs
  buildUpdatePayload = (rule) => {
    return {
      sellingPriceBusinessRuleId: rule.sellingPriceBusinessRuleId, // must be present
      name: (rule.name || "").trim(),
      description: rule.description || null,
      priority: Number(rule.priority || 0),
      isActive: !!rule.isActive,
      storeId: this.context?.storeId,
      sellingPriceRuleTiers: (rule.sellingPriceRuleTiers || []).map(
        (t, idx) => ({
          sellingPriceRuleTierId:
            t.sellingPriceRuleTierId || "00000000-0000-0000-0000-000000000000", // if existing tier has id; otherwise server will create new IDs
          minQtyInclusive:
            t.minQtyInclusive == null ? null : Number(t.minQtyInclusive),
          maxQtyInclusive:
            t.maxQtyInclusive == null ? null : Number(t.maxQtyInclusive),
          discountPercentOfMrp:
            t.discountPercentOfMrp == null
              ? null
              : Number(t.discountPercentOfMrp),
          seqNo: t.seqNo == null ? idx + 1 : Number(t.seqNo),
        })
      ),
    };
  };

  // -----------------------
  // API operations
  // -----------------------
  loadRules = async () => {
    this.setState({ loading: true, message: null });
    try {
      const storeId = this.context?.storeId;
      if (!storeId) {
        this.setState({
          loading: false,
          message: "StoreId not available in context.",
        });
        return;
      }
      const url = `${API_BASE}?storeId=${storeId}`;
      console.debug("GET rules url=", url);
      const res = await axios.get(url);
      const rules = (res.data || []).map((r) => ({
        sellingPriceBusinessRuleId: r.sellingPriceBusinessRuleId ?? null,
        name: r.name ?? "",
        description: r.description ?? "",
        priority: r.priority ?? 10,
        isActive: r.isActive ?? true,
        sellingPriceRuleTiers: (r.sellingPriceRuleTiers ?? []).map((t) => ({
          sellingPriceRuleTierId: t.sellingPriceRuleTierId ?? null,
          minQtyInclusive: t.minQtyInclusive ?? null,
          maxQtyInclusive: t.maxQtyInclusive ?? null,
          discountPercentOfMrp: t.discountPercentOfMrp ?? null,
          seqNo: t.seqNo ?? 0,
        })),
      }));
      this.setState({ rules, loading: false });
    } catch (err) {
      console.error("loadRules", err);
      this.setState({ loading: false, message: "Failed to load rules." });
    }
  };

  openCreate = () =>
    this.setState({
      showModal: true,
      isEditing: false,
      editRule: this.emptyRule(),
    });

  openEdit = async (id) => {
    this.setState({ loading: true, message: null });
    try {
      const storeId = this.context?.storeId;
      if (!storeId) {
        this.setState({
          loading: false,
          message: "StoreId not available in context.",
        });
        return;
      }
      const url = `${API_BASE}/${id}?storeId=${storeId}`;
      console.debug("GET rule url=", url);
      const res = await axios.get(url);
      const r = res.data;
      const rule = {
        sellingPriceBusinessRuleId: r.sellingPriceBusinessRuleId ?? null,
        name: r.name ?? "",
        description: r.description ?? "",
        priority: r.priority ?? 10,
        isActive: r.isActive ?? true,
        sellingPriceRuleTiers: (r.sellingPriceRuleTiers ?? []).map((t) => ({
          sellingPriceRuleTierId: t.sellingPriceRuleTierId ?? null,
          minQtyInclusive: t.minQtyInclusive ?? null,
          maxQtyInclusive: t.maxQtyInclusive ?? null,
          discountPercentOfMrp: t.discountPercentOfMrp ?? null,
          seqNo: t.seqNo ?? 0,
        })),
      };
      this.setState({
        editRule: rule,
        isEditing: true,
        showModal: true,
        loading: false,
      });
    } catch (err) {
      console.error("openEdit", err);
      this.setState({ loading: false, message: "Failed to load rule." });
    }
  };

  closeModal = () =>
    this.setState({
      showModal: false,
      editRule: this.emptyRule(),
      isEditing: false,
    });

  saveRule = async () => {
    const { isEditing, editRule } = this.state;

    // basic validation
    if (!editRule || !editRule.name || !editRule.name.trim()) {
      return Swal.fire("Validation", "Name is required", "warning");
    }

    const vt = this.validateTiers(editRule);
    if (!vt.ok) return Swal.fire("Validation", vt.message, "warning");

    const storeId = this.context?.storeId;
    if (!storeId)
      return Swal.fire("Error", "StoreId not available in context.", "error");

    const payload = isEditing
      ? this.buildUpdatePayload(editRule)
      : this.buildCreatePayload(editRule);

    const url = isEditing
      ? `${API_BASE}/${payload.sellingPriceBusinessRuleId}`
      : API_BASE;

    console.info("Saving rule -> URL:", url);
    console.info("Payload:", payload);

    this.setState({ saving: true });
    try {
      // use validateStatus to allow reading 400 response body
      const res = isEditing
        ? await axios.put(url, payload, { validateStatus: () => true })
        : await axios.post(url, payload, { validateStatus: () => true });

      // If not 2xx, show server body with details
      if (res.status < 200 || res.status >= 300) {
        const serverBody = res.data;
        console.warn("Server returned non-2xx:", res.status, serverBody);
        let userMsg = `HTTP ${res.status}`;

        if (serverBody) {
          if (typeof serverBody === "string") userMsg += `: ${serverBody}`;
          else if (serverBody.errors) {
            // ASP.NET Core ModelState format
            const errs = [];
            Object.keys(serverBody.errors).forEach((k) =>
              errs.push(...serverBody.errors[k])
            );
            userMsg += `: ${errs.join(" ; ")}`;
          } else if (serverBody.title || serverBody.detail) {
            userMsg += `: ${serverBody.title ?? ""} ${serverBody.detail ?? ""}`;
          } else {
            userMsg += `: ${JSON.stringify(serverBody)}`;
          }
        }

        Swal.fire({
          icon: "error",
          title: "Save failed (server)",
          html: `<pre style="text-align:left;white-space:pre-wrap">${userMsg}</pre><p>Open console/network tab for request/response details.</p>`,
          width: 800,
        });
        console.debug("Request (sent):", { url, payload });
        console.debug("Response object:", res);
        return;
      }

      // success
      Swal.fire(
        "Saved",
        isEditing ? "Rule updated successfully" : "Rule created successfully",
        "success"
      );
      this.closeModal();
      await this.loadRules();
    } catch (err) {
      console.error("saveRule exception:", err);
      let userMsg = err.message || "Failed to save rule";
      if (err.response) {
        userMsg = `HTTP ${err.response.status}: ${JSON.stringify(
          err.response.data
        )}`;
      }
      Swal.fire("Error", userMsg, "error");
    } finally {
      this.setState({ saving: false });
    }
  };

  deleteRule = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete rule?",
      text: "This will permanently delete the rule and its tiers.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;

    this.setState({ loading: true });
    try {
      const storeId = this.context?.storeId;
      if (!storeId) {
        this.setState({
          loading: false,
          message: "StoreId not available in context.",
        });
        return;
      }
      const url = `${API_BASE}/${id}?storeId=${storeId}`;
      await axios.delete(url);
      Swal.fire("Deleted", "Rule removed", "success");
      await this.loadRules();
    } catch (err) {
      console.error("deleteRule", err);
      Swal.fire("Error", "Failed to delete rule", "error");
      this.setState({ loading: false });
    }
  };

  // -----------------------
  // Form handlers
  // -----------------------
  handleRuleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prev) => ({
      editRule: {
        ...prev.editRule,
        [name]:
          type === "checkbox"
            ? checked
            : name === "priority"
            ? parseInt(value, 10) || 0
            : value,
      },
    }));
  };

  handleTierChange = (idx, e) => {
    const { name, value } = e.target;
    this.setState((prev) => {
      const tiers = [...(prev.editRule.sellingPriceRuleTiers || [])];
      let v = value === "" ? null : value;
      if (v !== null) {
        if (name.toLowerCase().includes("qty") || name === "seqNo")
          v = parseInt(v, 10);
        else v = parseFloat(v);
      }
      tiers[idx] = { ...tiers[idx], [name]: v };
      return { editRule: { ...prev.editRule, sellingPriceRuleTiers: tiers } };
    });
  };

  addTier = () => {
    this.setState((prev) => {
      const tiers = [...(prev.editRule.sellingPriceRuleTiers || [])];
      tiers.push({
        sellingPriceRuleTierId: null,
        minQtyInclusive: 1,
        maxQtyInclusive: 1,
        discountPercentOfMrp: null,
        seqNo: tiers.length + 1,
      });
      return { editRule: { ...prev.editRule, sellingPriceRuleTiers: tiers } };
    });
  };

  removeTier = (idx) => {
    this.setState((prev) => {
      const tiers = [...(prev.editRule.sellingPriceRuleTiers || [])];
      tiers.splice(idx, 1);
      return { editRule: { ...prev.editRule, sellingPriceRuleTiers: tiers } };
    });
  };

  // -----------------------
  // Render
  // -----------------------
  render() {
    const { rules, loading, showModal, editRule, message, saving } = this.state;

    return (
      <div className="container mt-3">
        <h4>Selling Price Rules</h4>

        {message && <Alert variant="info">{message}</Alert>}

        <div className="d-flex mb-2">
          <Button className="me-2" onClick={this.openCreate}>
            + Create
          </Button>
          <Button variant="outline-secondary" onClick={() => this.loadRules()}>
            <BiRefresh /> Refresh
          </Button>
        </div>

        {loading ? (
          <LoadSpinner open="true" />
        ) : (
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Priority</th>
                <th>Active</th>
                <th>Tiers</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center text-muted">
                    No rules found
                  </td>
                </tr>
              ) : (
                rules.map((r) => (
                  <tr key={r.sellingPriceBusinessRuleId}>
                    <td>{r.name}</td>
                    <td>{r.priority}</td>
                    <td>{String(r.isActive)}</td>
                    <td>
                      {(r.sellingPriceRuleTiers || []).length ? (
                        <ul className="mb-0">
                          {r.sellingPriceRuleTiers.map((t) => (
                            <li
                              key={
                                t.sellingPriceRuleTierId ??
                                `${r.sellingPriceBusinessRuleId}-${t.seqNo}`
                              }
                            >
                              Qty [{t.minQtyInclusive ?? "-"} -{" "}
                              {t.maxQtyInclusive ?? "∞"}] —{" "}
                              {`${t.discountPercentOfMrp ?? "-"}%`}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        onClick={() =>
                          this.openEdit(r.sellingPriceBusinessRuleId)
                        }
                      >
                        Edit
                      </Button>{" "}
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() =>
                          this.deleteRule(r.sellingPriceBusinessRuleId)
                        }
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        <Modal show={showModal} onHide={this.closeModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {this.state.isEditing ? "Edit Rule" : "Create Rule"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="mb-2">
                <Col md={8}>
                  <FloatingLabel label="Name">
                    <Form.Control
                      name="name"
                      value={editRule.name}
                      onChange={this.handleRuleChange}
                    />
                  </FloatingLabel>
                </Col>
                <Col md={2}>
                  <FloatingLabel label="Priority">
                    <Form.Control
                      name="priority"
                      type="number"
                      readOnly
                      value={editRule.priority}
                      onChange={this.handleRuleChange}
                    />
                  </FloatingLabel>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Form.Check
                    type="checkbox"
                    label="Is Active"
                    name="isActive"
                    checked={editRule.isActive}
                    onChange={this.handleRuleChange}
                  />
                </Col>
              </Row>

              <FloatingLabel label="Description" className="mb-2">
                <Form.Control
                  as="textarea"
                  name="description"
                  value={editRule.description}
                  onChange={this.handleRuleChange}
                  style={{ height: "80px" }}
                />
              </FloatingLabel>

              <hr />
              <h6 className="mt-3 mb-2">Tiers</h6>
              {(editRule.sellingPriceRuleTiers || []).length > 0 && (
                <Row className="fw-bold text-secondary border-bottom mb-2 pb-1">
                  <Col md={2}>Min Qty</Col>
                  <Col md={2}>Max Qty</Col>
                  <Col md={2}>% Off</Col>
                  <Col md={2}>Seq No</Col>
                  <Col md={4}>Action</Col>
                </Row>
              )}

              {(editRule.sellingPriceRuleTiers || []).map((t, idx) => (
                <Row key={idx} className="mb-2 g-2 align-items-center">
                  <Col md={2}>
                    <Form.Control
                      name="minQtyInclusive"
                      value={t.minQtyInclusive ?? ""}
                      onChange={(e) => this.handleTierChange(idx, e)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Control
                      name="maxQtyInclusive"
                      value={t.maxQtyInclusive ?? ""}
                      onChange={(e) => this.handleTierChange(idx, e)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Control
                      name="discountPercentOfMrp"
                      value={t.discountPercentOfMrp ?? ""}
                      onChange={(e) => this.handleTierChange(idx, e)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Control
                      name="seqNo"
                      value={t.seqNo ?? idx + 1}
                      onChange={(e) => this.handleTierChange(idx, e)}
                    />
                  </Col>
                  <Col md={4}>
                    <Button
                      variant="outline-danger"
                      onClick={() => this.removeTier(idx)}
                    >
                      Remove
                    </Button>
                  </Col>
                </Row>
              ))}

              <div className="mt-2">
                <Button onClick={this.addTier}>Add Tier</Button>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeModal}>
              Close
            </Button>
            <Button onClick={this.saveRule} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
